<template>
  <div v-if="momentum">
    <svg
      class="net-momentum"
      viewBox="0 0 42 42"
      width="42"
      height="42"
      :class="{
        'is-selected': isSelected || isActive || isHighlighted,
        'is-connected': isElementSelected
      }"
    >
      <g transform="translate(22, 22)">
        <g
          :transform="
            !isPositive
              ? `translate(-${u / 1.5},-${u / 2})`
              : `translate(-${u / 2},-${u / 1.5})`
          "
          @click="onClick"
          @pointerdown="onMouseDown"
          @pointerover="onMouseOver"
        >
          <circle :cx="u / 2" :cy="u / 2" :r="u / 2" class="net-momentum__path" />

          <!-- TIP -->
          <!-- direction depends on momentum value v-if="momentum.points > 0 || momentum.percent > 0" -->
          <path
            v-if="!isPositive"
            class="net-momentum__tip"
            :d="`M${u / 2},${u - 4} L${u / 1.5},${u} L${u / 2},${u + 4} Z`"
          />
          <path
            v-if="isPositive"
            class="net-momentum__tip"
            :d="`M${u + 4},${u / 2} L${u},${u / 1.5} L${u - 4},${u / 2} Z`"
          />
        </g>
      </g>
    </svg>

    <template v-if="isSelected && netMode == 'edit'">
      <momentum-popup-menu
        v-if="popupVisible"
        :momentum-id="momentum.id"
        :x="elementPosition.x + props.x - 10"
        :y="elementPosition.y + props.y - 25"
        @close="closePopupmenu"
      ></momentum-popup-menu>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, inject, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";

import MomentumPopupMenu from "./MomentumPopupMenu.vue";

import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useMomentumStore } from "@editor/items/momentum/momentums.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";

import type { Handleable } from "@core/types/Events";
import type { MomentumItem } from "@editor/items/momentum/MomentumItem";

import { keyElementValues } from "@editor/net/netCanvasInjectionKeys";
import { evaluateRelation } from "@editor/items/relation/Relation";
import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import eventHub from "@/helpers/utility/Events";

const props = withDefaults(
  defineProps<{
    netMode: "edit" | "view" | "choose" | "influence";
    elementPosition: { x: number; y: number },
    x?: number;
    y?: number;
    elementType: "target" | "resource";
    elementId: number;
    selection: CanvasSelection;
  }>(),
  {
    x: 0,
    y: 0
  }
);

const emit = defineEmits<{
  (e: "select", momentum: MomentumItem): void;
  (e: "hoveron"): void;
  (e: "hoveroff"): void;
}>();

const u = ref(30);

const route = useRoute();

const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const selectionStore = useSelectionStore();

const items = computed(() => {
  return {
    target: targetStore.items,
    resource: resourceStore.items
  };
});

const element = computed(() => {
  return items.value[props.elementType][props.elementId];
});

const injectedElementValues = inject(keyElementValues);
const elementValue = computed((): number => {
  const calculatedValue = injectedElementValues?.value?.[element.value.uid];
  return Number(calculatedValue?.value ?? element.value?.value ?? 0);
});

const momentum = computed(() => {
  return useMomentumStore().byType[props.elementType]?.[props.elementId] ?? null;
});

// ================
// Computed state properties

const currentValue = computed(() => {
  return evaluateRelation(momentum.value, elementValue.value);
});

const isPositive = computed(() => {
  return currentValue.value > 0;
});

const isSelected = computed(() => {
  const selectedItem = selectionStore.selectedItem;
  if (selectedItem == null) return false;
  return (
    selectedItem.type === "momentum" && selectedItem.id === momentum.value.id
  );
});

const isActive = computed(() => {
  if (route.meta.areaName != "simulation") return false;

  return props.selection.active.has(momentum.value?.uid);
});

const isHighlighted = computed(() => {
  return props.selection.highlighted.has(momentum.value?.uid);
});

const isElementSelected = computed(() => {
  return props.selection.selected.has(element.value.uid);
});

// ================
// POPUP

const popupVisible = ref(false);

function openPopupIfRequested(uid: string) {
  if (uid === momentum.value?.uid) {
    openPopupmenu();
  }
}
eventHub.on("popupmenu-request", openPopupIfRequested);
onBeforeUnmount(() => {
  eventHub.off("popupmenu-request", openPopupIfRequested);
});

function openPopupmenu() {
  popupVisible.value = true;
}
function closePopupmenu() {
  popupVisible.value = false;
}

// ================
// Event handlers

function onMouseDown(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.hasBeenHandled = true;
}

function onClick(event: MouseEvent & Handleable) {
  event.hasBeenHandled = true;
  emit("select", momentum.value);
}

function onMouseOver(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.hasBeenHandled = true;
  emit("hoveron");
}
</script>

<style lang="scss">
.net-momentum {
  --momentum-color: var(--color-relation-standard);

  &__path {
    stroke: var(--momentum-color);
    stroke-width: 2px;
    fill: transparent;
  }
  &__tip {
    fill: var(--momentum-color);
  }

  &.is-connected {
    --momentum-color: var(--color-text-medium);
  }

  &:hover,
  &.is-selected,
  &.is-highlighted {
    --momentum-color: var(--color-action);
  }
}
</style>
